import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { USE_CASES, REFERRAL_SOURCES, COUNTRIES } from '../../constants/signupOptions';
import type { SignupErrors, SignupFormData } from './signupValidation';
import LegalNoticeModal from './LegalNoticeModal';

interface SignupFormProps {
  styles: Record<string, string>;
  formData: SignupFormData;
  error: SignupErrors;
  loading: boolean;
  showPassword: boolean;
  showConfirmPassword: boolean;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onBlur: (event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onTogglePassword: () => void;
  onToggleConfirmPassword: () => void;
}

export default function SignupForm({
  styles,
  formData,
  error,
  loading,
  showPassword,
  showConfirmPassword,
  onSubmit,
  onInputChange,
  onBlur,
  onTogglePassword,
  onToggleConfirmPassword,
}: SignupFormProps) {
  const [openNotice, setOpenNotice] = useState<'terms' | 'privacy' | null>(null);

  const openLegalNotice = (notice: 'terms' | 'privacy') => (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenNotice(notice);
  };

  return (
    <div className={styles.rightPanel}>
      <form className={styles.formCard} onSubmit={onSubmit} autoComplete="on" noValidate>
        <h2 className={styles.formTitle}>Create Account</h2>
        <p className={styles.formSubtitle}>Start your legal intelligence journey</p>

        {error.general && <div className={styles.errorMsg}>{error.general}</div>}

        <div className={styles.formGroup}>
          <div className={styles.floatingLabelGroup}>
            <input
              type="text"
              name="fullName"
              id="fullName"
              autoComplete="name"
              value={formData.fullName}
              onChange={onInputChange}
              onBlur={onBlur}
              className={error.fullName ? styles.inputError : ''}
              required
              disabled={loading}
            />
            <label htmlFor="fullName" className={formData.fullName ? styles.hasValue : ''}>Full Name *</label>
          </div>
          {error.fullName && <span className={styles.errorText}>{error.fullName}</span>}
        </div>

        <div className={styles.formGroup}>
          <div className={styles.floatingLabelGroup}>
            <input
              type="email"
              name="email"
              id="email"
              autoComplete="email"
              value={formData.email}
              onChange={onInputChange}
              onBlur={onBlur}
              className={error.email ? styles.inputError : ''}
              required
              disabled={loading}
            />
            <label htmlFor="email" className={formData.email ? styles.hasValue : ''}>Email Address *</label>
          </div>
          {error.email && <span className={styles.errorText}>{error.email}</span>}
        </div>

        <div className={styles.formGroup}>
          <div className={styles.floatingLabelGroup}>
            <input
              type="tel"
              name="phoneNumber"
              id="phoneNumber"
              autoComplete="tel"
              value={formData.phoneNumber}
              onChange={onInputChange}
              onBlur={onBlur}
              className={error.phoneNumber ? styles.inputError : ''}
              disabled={loading}
            />
            <label htmlFor="phoneNumber" className={formData.phoneNumber ? styles.hasValue : ''}>Phone Number (Optional)</label>
          </div>
          {error.phoneNumber && <span className={styles.errorText}>{error.phoneNumber}</span>}
        </div>

        <div className={styles.formGroup}>
          <div className={styles.floatingLabelGroup}>
            <input
              type="text"
              name="company"
              id="company"
              autoComplete="organization"
              value={formData.company}
              onChange={onInputChange}
              onBlur={onBlur}
              className={error.company ? styles.inputError : ''}
              required
              disabled={loading}
            />
            <label htmlFor="company" className={formData.company ? styles.hasValue : ''}>Company/Organization *</label>
          </div>
          {error.company && <span className={styles.errorText}>{error.company}</span>}
        </div>

        <div className={styles.formGroup}>
          <div className={styles.floatingLabelGroup}>
            <input
              type="text"
              name="jobTitle"
              id="jobTitle"
              autoComplete="organization-title"
              value={formData.jobTitle}
              onChange={onInputChange}
              onBlur={onBlur}
              className={error.jobTitle ? styles.inputError : ''}
              required
              disabled={loading}
            />
            <label htmlFor="jobTitle" className={formData.jobTitle ? styles.hasValue : ''}>Job Title *</label>
          </div>
          {error.jobTitle && <span className={styles.errorText}>{error.jobTitle}</span>}
        </div>

        <div className={styles.formGroup}>
          <select
            name="useCase"
            id="useCase"
            value={formData.useCase}
            onChange={onInputChange}
            onBlur={onBlur}
            className={`${error.useCase ? styles.inputError : ''} ${styles.formSelect}`}
            required
            disabled={loading}
          >
            <option value="">Select Your Primary Use Case *</option>
            {USE_CASES.map((useCase) => (
              <option key={useCase.value} value={useCase.value}>
                {useCase.label}
              </option>
            ))}
          </select>
          {error.useCase && <span className={styles.errorText}>{error.useCase}</span>}
        </div>

        <div className={styles.formGroup}>
          <select
            name="referralSource"
            id="referralSource"
            value={formData.referralSource}
            onChange={onInputChange}
            onBlur={onBlur}
            className={`${error.referralSource ? styles.inputError : ''} ${styles.formSelect}`}
            required
            disabled={loading}
          >
            <option value="">How did you hear about us? *</option>
            {REFERRAL_SOURCES.map((source) => (
              <option key={source.value} value={source.value}>
                {source.label}
              </option>
            ))}
          </select>
          {error.referralSource && <span className={styles.errorText}>{error.referralSource}</span>}
        </div>

        <div className={styles.formGroup}>
          <select
            name="country"
            id="country"
            value={formData.country}
            onChange={onInputChange}
            onBlur={onBlur}
            className={`${error.country ? styles.inputError : ''} ${styles.formSelect}`}
            required
            disabled={loading}
          >
            <option value="">Select Your Country *</option>
            {COUNTRIES.map((country) => (
              <option key={country.value} value={country.value}>
                {country.label}
              </option>
            ))}
          </select>
          {error.country && <span className={styles.errorText}>{error.country}</span>}
        </div>

        <div className={styles.formGroup}>
          <div className={styles.floatingLabelGroup}>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={onInputChange}
              onBlur={onBlur}
              className={error.password ? styles.inputError : ''}
              required
              disabled={loading}
            />
            <label htmlFor="password" className={formData.password ? styles.hasValue : ''}>Password *</label>
            <button
              type="button"
              className={styles.passwordToggle}
              onClick={onTogglePassword}
              tabIndex={-1}
              aria-label="Toggle password visibility"
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
            </button>
          </div>
          {error.password && <span className={styles.errorText}>{error.password}</span>}
          {!error.password && formData.password && (
            <span className={styles.fieldHint}>
              Min 8 characters, include uppercase, lowercase, number & special character
            </span>
          )}
        </div>

        <div className={styles.formGroup}>
          <div className={styles.floatingLabelGroup}>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              id="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={onInputChange}
              onBlur={onBlur}
              className={error.confirmPassword ? styles.inputError : ''}
              required
              disabled={loading}
            />
            <label htmlFor="confirmPassword" className={formData.confirmPassword ? styles.hasValue : ''}>
              Confirm Password *
            </label>
            <button
              type="button"
              className={styles.passwordToggle}
              onClick={onToggleConfirmPassword}
              tabIndex={-1}
              aria-label="Toggle confirm password visibility"
            >
              <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
            </button>
          </div>
          {error.confirmPassword && <span className={styles.errorText}>{error.confirmPassword}</span>}
        </div>

        <div className={styles.formGroup} style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              name="termsAccepted"
              checked={formData.termsAccepted}
              onChange={onInputChange}
              onBlur={onBlur}
              disabled={loading}
            />
            <span className={styles.checkboxLabel}>
              I accept the{' '}
              <button type="button" className={styles.legalNoticeLink} onClick={openLegalNotice('terms')}>
                Terms of Service
              </button>
              {' '}*
            </span>
          </label>
          {error.termsAccepted && <span className={styles.errorText}>{error.termsAccepted}</span>}
        </div>

        <div className={styles.formGroup} style={{ marginBottom: '1rem' }}>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              name="privacyAccepted"
              checked={formData.privacyAccepted}
              onChange={onInputChange}
              onBlur={onBlur}
              disabled={loading}
            />
            <span className={styles.checkboxLabel}>
              I acknowledge the{' '}
              <button type="button" className={styles.legalNoticeLink} onClick={openLegalNotice('privacy')}>
                Privacy Policy
              </button>
              {' '}*
            </span>
          </label>
          {error.privacyAccepted && <span className={styles.errorText}>{error.privacyAccepted}</span>}
        </div>

        <div className={styles.formGroup} style={{ marginBottom: '1.5rem' }}>
          <label className={styles.checkboxRow} style={{ marginBottom: 0 }}>
            <input
              type="checkbox"
              name="marketingConsent"
              checked={formData.marketingConsent}
              onChange={onInputChange}
              disabled={loading}
            />
            <span className={styles.checkboxLabel}>
              I would like to receive updates, news, and promotional content from VerdeKai (Optional)
            </span>
          </label>
        </div>

        <button type="submit" className={styles.signInBtn} disabled={loading}>
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>

        <div className={styles.signupPrompt}>
          Already have an account? <a href="/login">Sign in</a>
        </div>
      </form>
      <LegalNoticeModal
        isOpen={openNotice !== null}
        title={openNotice === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
        onClose={() => setOpenNotice(null)}
      />
    </div>
  );
}
